import React, { useEffect, useMemo, useRef, useState } from "react"
import PubSub from "pubsub-js"
import { CheckCircleIcon } from "@heroicons/react/24/solid"
import useT from "../hooks/useT"
import useAsync from "../hooks/useAsync"
import AddressForm, { EMPTY_ADDRESS, addressLine } from "../account/AddressForm"
import { isValidAddress } from "aldoo-ra/Store/address-validation"
import { UserAddresses, AddAddress, UpdateAddress, isSignedIn } from "../storeApi"
import Spinner from "../ui/Spinner"

const FORM_ID = "sf-checkout-address"

/**
 * Checkout step "billing": billing / delivery address. A signed-in buyer with
 * saved addresses sees the default one to check, Change opens the address book
 * (a new one is stored there); the "I need an invoice" switch turns on the
 * invoice-details step. Continue is the flow footer button (it submits the form).
 */
const ADDRESS_FIELDS = ["name", "line1", "line2", "city", "state", "postal_code", "country", "phone"]
const addressKey = (address) => ADDRESS_FIELDS.map((field) => String(address?.[field] ?? "").trim().replace(/\s+/g, " ").toLowerCase()).join("|")

/** The address book without repeated entries; the default one wins its slot. */
function uniqueAddresses(addresses) {
  const byKey = new Map()
  for (const address of addresses) {
    const key = addressKey(address)
    const kept = byKey.get(key)
    if (!kept) byKey.set(key, address)
    else if (address.isDefault) byKey.set(key, address)
  }
  return [...byKey.values()]
}

/**
 * The address comes before the delivery: the couriers price the parcel for
 * it, and its name and phone are who receives it — at the door, or at the
 * office / locker the delivery step picks.
 */
export default function BillingStep(props) {
  return <AddressStep {...props} />
}

// `onComplete(address, needsInvoice)`: the address handed back instead of going to the next step
// (the delivery step asking it for a seller's parcel), its button that step's (`stepId`)
export function AddressStep({ store, isActive, stepData, onComplete = null, stepId = "billing" }) {
  const t = useT()
  const signedIn = isSignedIn()
  const { result, pending } = useAsync(() => UserAddresses(), [], { enabled: signedIn })
  // older accounts collected a copy of the same address per order (the add
  // action now matches an existing one); never show the same address twice
  const saved = useMemo(() => uniqueAddresses(result?.addresses || []), [result])
  const [mode, setMode] = useState("pick")
  const [selected, setSelected] = useState(null)
  // the saved address shown alone, to be checked; Change lists the address book
  const [listing, setListing] = useState(false)
  // coming back to the step: the address chosen before is one of the saved
  // ones, so the list shows it selected, as on the first visit; only an
  // address that is not in the book reopens the form with it
  useEffect(() => {
    if (!stepData?.address || !saved.length) return
    const previous = saved.find((a) => addressKey(a) === addressKey(stepData.address))
    if (previous) return setSelected(previous)
    setMode("new")
  }, [saved])
  const [needsInvoice, setNeedsInvoice] = useState(() => stepData?.needsInvoice === true)
  const [busy, setBusy] = useState(false)
  // a saved address that fails the address rules opens in the form to be fixed
  const [fixing, setFixing] = useState(null)
  const submitRef = useRef(null)
  const defaultAddress = saved.find((a) => a.isDefault) || saved[0]
  const picking = signedIn && saved.length > 0 && mode === "pick"
  const chosen = selected || defaultAddress

  const complete = (address) => {
    if (onComplete) return onComplete(address, needsInvoice)
    PubSub.publish("PURCHASE_FLOW_NEXT", { stepData: { address, needsInvoice } })
  }

  const submitNew = async (address) => {
    setBusy(true)
    try {
      // the saved address that was fixed is corrected in the book, not copied
      if (signedIn && fixing?._id) await UpdateAddress(fixing._id, address).catch(() => {})
      else if (signedIn) await AddAddress(address).catch(() => {})
      complete(address)
    } finally {
      setBusy(false)
    }
  }

  const onContinue = () => {
    if (picking) {
      if (!chosen) return
      if (!isValidAddress(chosen)) {
        setFixing(chosen)
        return setMode("new")
      }
      return complete({ ...EMPTY_ADDRESS, ...chosen, _id: undefined, isDefault: undefined })
    }
    document.getElementById(FORM_ID)?.requestSubmit()
  }
  submitRef.current = onContinue

  useEffect(() => {
    if (!isActive) return
    const loading = signedIn && pending && !result
    PubSub.publish("PURCHASE_FLOW_UPDATE_BUTTON", {
      stepId,
      canContinue: !loading && !busy && (!picking || !!chosen),
      loading: busy,
      onContinue: () => submitRef.current?.(),
    })
  }, [isActive, pending, result, busy, picking, chosen, needsInvoice])

  const invoiceToggle = (
    <label className="md:col-span-2 flex items-center gap-3 rounded-lg bg-neutral px-4 py-3 cursor-pointer">
      <input type="checkbox" name="needsInvoice" checked={needsInvoice} onChange={(e) => setNeedsInvoice(e.target.checked)} className="w-5 h-5 accent-primary" />
      <span className="text-[15px]">{t`I need an invoice for a company`}</span>
    </label>
  )

  if (signedIn && pending && !result) return <Spinner className="py-16" />

  if (picking && !listing && chosen) {
    return (
      <div className="flex flex-col gap-4 px-4 py-6" data-testid="checkout-billing">
        <p className="m-0 text-[15px]">{t`Check your delivery address`}</p>
        <div className="flex items-start gap-4 rounded-lg border border-primary ring-1 ring-primary px-4 py-4" data-testid="checkout-address-review">
          <CheckCircleIcon className="w-6 h-6 shrink-0 text-primary" aria-hidden />
          <span className="flex-1 min-w-0">
            <span className="block font-semibold">{chosen.name}</span>
            <span className="block text-[14px] text-text_secondary">{addressLine({ ...chosen, name: undefined, phone: undefined })}</span>
            {chosen.phone && <span className="block text-[14px] text-text_secondary">{chosen.phone}</span>}
          </span>
          <button type="button" onClick={() => setListing(true)} className="shrink-0 font-bold text-primary hover:underline" data-testid="checkout-address-change">
            {t`Change`}
          </button>
        </div>
        {invoiceToggle}
      </div>
    )
  }

  if (picking) {
    return (
      <div className="flex flex-col gap-4 px-4 py-6" data-testid="checkout-billing">
        <p className="m-0 text-[15px]">{t`Choose a delivery address`}</p>
        <ul className="m-0 p-0 list-none flex flex-col gap-3" role="radiogroup">
          {saved.map((address) => (
            <li key={address._id}>
              {/* chosen as a delivery option is: a radio, and the thick primary outline (nothing moves) */}
              <button type="button" role="radio" aria-checked={address === chosen} onClick={() => setSelected(address)} className={`w-full flex items-center gap-4 rounded-lg border px-4 py-4 text-left ${address === chosen ? "border-primary ring-1 ring-primary" : "border-bordered hover:border-text"}`} data-testid="saved-address" data-selected={address === chosen ? "yes" : "no"}>
                <span className={`w-5 h-5 shrink-0 rounded-full border-2 flex items-center justify-center ${address === chosen ? "border-primary" : "border-bordered"}`} aria-hidden>
                  {address === chosen && <span className="w-2.5 h-2.5 rounded-full bg-primary" />}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block font-semibold">{address.name}</span>
                  <span className="block text-[14px] text-text_secondary">{addressLine({ ...address, name: undefined })}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
        {invoiceToggle}
        <button type="button" onClick={() => setMode("new")} className="self-start font-bold text-primary hover:underline">
          {t`Add a new address`}
        </button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 px-4 py-6" data-testid="checkout-billing">
      <AddressForm key={fixing ? "fixing" : "new"} formId={FORM_ID} initial={fixing ? { ...EMPTY_ADDRESS, ...fixing, _id: undefined, isDefault: undefined } : stepData?.address || undefined} busy={busy} hideActions onSubmit={submitNew}>
        {invoiceToggle}
      </AddressForm>
      {signedIn && saved.length > 0 && (
        <button type="button" onClick={() => setMode("pick")} className="self-start font-bold text-primary hover:underline">
          {t`Choose a saved address`}
        </button>
      )}
    </div>
  )
}
